import React, { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Monitor, Settings, ArrowRight } from 'lucide-react';

export const ShowcaseSection = () => {
  const [activeTab, setActiveTab] = useState('storefront');

  return (
    <section id="showcase" className="py-20 bg-gradient-to-br from-[#F7F9FC] to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center space-y-4 mb-16">
          <Badge className="bg-[#5750f1] text-white px-4 py-2 text-sm">
            Ver DigiStore en Acción
          </Badge>
          <h2 className="text-4xl lg:text-5xl font-bold text-[#0A2540]">
            Tienda Bonita, Clientes contentos
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Tanto para tus clientes como para la administración de tu negocio, contamos con interfaces modernas que llevaran a tu negocio y productos al siguiente nivel.
          </p>
        </div>

        {/* Showcase Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2 max-w-md mx-auto mb-12 bg-white border border-gray-200">
            <TabsTrigger 
              value="storefront" 
              className="flex items-center space-x-2 data-[state=active]:bg-[#5750f1] data-[state=active]:text-white"
            >
              <Monitor size={18} />
              <span>Vista de la Tienda</span>
            </TabsTrigger>
            <TabsTrigger 
              value="admin" 
              className="flex items-center space-x-2 data-[state=active]:bg-[#5750f1] data-[state=active]:text-white"
            >
              <Settings size={18} />
              <span>Panel de Administración</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="storefront" className="space-y-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Content */}
              <div className="space-y-6">
                <div>
                  <h3 className="text-3xl font-bold text-[#0A2540] mb-4">
                    Experiencia de Cliente Profesional
                  </h3>
                  <p className="text-lg text-gray-600 leading-relaxed">
                    Tus clientes verán una tienda bonita y profesional con imágenes de alta calidad, especificaciones detalladas y opciones de intercambio sin complicaciones.
                  </p>
                </div>

                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-[#5750f1] rounded-full mt-2"></div>
                    <div>
                      <div className="font-semibold text-[#0A2540]">Diseño Responsivo</div>
                      <div className="text-gray-600">Perfecto en escritorio, tablet y móvil</div>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-[#5750f1] rounded-full mt-2"></div>
                    <div>
                      <div className="font-semibold text-[#0A2540]">Búsqueda y Filtros</div>
                      <div className="text-gray-600">Ayuda a tus clientes a encontrar exactamente lo que necesitan</div>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-[#5750f1] rounded-full mt-2"></div>
                    <div>
                      <div className="font-semibold text-[#0A2540]">Intercambio Integrado</div>
                      <div className="text-gray-600">Cotizaciones instantáneas para dispositivos antiguos</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Screenshot */}
              <Card className="overflow-hidden shadow-2xl transform hover:scale-105 transition-transform duration-500">
                <CardContent className="p-0">
                  <img
                    //src="https://images.unsplash.com/photo-1642142785744-261a5f663d12?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwxfHxlY29tbWVyY2UlMjBwcm9kdWN0JTIwcGFnZXxlbnwwfHx8fDE3NTc4ODc1MjZ8MA&ixlib=rb-4.1.0&q=85"
                    src="/digistore_shot.png"
                    alt="DigiStore Public Product Page"
                    className="w-full h-auto"
                  />
                  <div className="absolute top-4 right-4 bg-green-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
                    Tienda en vivo
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="admin" className="space-y-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Screenshot */}
              <Card className="overflow-hidden shadow-2xl transform hover:scale-105 transition-transform duration-500">
                <CardContent className="p-0">
                  <img
                    src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHw0fHxhbmFseXRpY3N8ZW58MHx8fHwxNzU3ODg3NTUzfDA&ixlib=rb-4.1.0&q=85"
                    alt="DigiStore Admin - Template Selection View"
                    className="w-full h-auto"
                  />
                  <div className="absolute top-4 right-4 bg-[#5750f1] text-white px-3 py-1 rounded-full text-sm font-semibold">
                    Panel de Administración
                  </div>
                </CardContent>
              </Card>

              {/* Content */}
              <div className="space-y-6">
                <div>
                  <h3 className="text-3xl font-bold text-[#0A2540] mb-4">
                    Panel de Administración Potente
                  </h3>
                  <p className="text-lg text-gray-600 leading-relaxed">
                    Gestiona tu negocio entero desde un panel intuitivo. Crea listados en segundos, rastrea pedidos y analiza el rendimiento.
                  </p>
                </div>

                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                    <div>
                      <div className="font-semibold text-[#0A2540]">Plantillas de un solo clic</div>
                      <div className="text-gray-600">Habilita tus productos en tiempo record!</div>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                    <div>
                      <div className="font-semibold text-[#0A2540]">Análisis en tiempo real</div>
                      <div className="text-gray-600">Rastrea ventas, visitantes y rendimiento</div>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                    <div>
                      <div className="font-semibold text-[#0A2540]">Gestión de Pedidos</div>
                      <div className="text-gray-600">Procesa pedidos y gestiona inventario</div>
                    </div>
                  </div>
                </div>

                <button className="flex items-center space-x-2 text-[#5750f1] hover:text-[#0042CC] font-semibold transition-colors">
                  <span>Ver más</span>
                  <ArrowRight size={18} />
                </button>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </section>
  );
};